import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { groupResults, resultHref, shouldSearch, useDebounced, useSearch, useViewer } from '@/lib/search';
import { ErrorNote, Spinner } from '@/components/Layout';

/** Every result for a search, grouped, with more per group than the header box shows. */
export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') ?? '');
  const dq = useDebounced(q, 200);
  const viewer = useViewer();
  const results = useSearch(dq, { limit: 120, perKind: 25 });
  const groups = groupResults(results.data ?? [], viewer.isStaff);

  useEffect(() => { setQ(params.get('q') ?? ''); }, [params]);
  useEffect(() => {
    if ((params.get('q') ?? '') !== dq) setParams(dq ? { q: dq } : {}, { replace: true });
  }, [dq]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6">
      <h1 className="text-3xl">Search</h1>
      <label className="relative block max-w-xl">
        <span className="sr-only">Search</span>
        <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
        <input className="input pl-9" autoFocus value={q} onChange={e => setQ(e.target.value)}
          placeholder={viewer.isStaff ? 'Name, member number, email, phone, postcode or payment reference' : 'Name, event or dojo'} />
      </label>
      {!shouldSearch(dq) ? <p className="text-ink-500">Type at least two letters, or a member number.</p>
        : results.isLoading ? <Spinner label="Searching" />
        : results.isError ? <ErrorNote>Search isn't working just now. Please try again.</ErrorNote>
        : groups.length === 0 ? (
          <p className="text-ink-500">Nothing found for “{dq.trim()}”.{!viewer.isStaff && ` You can find your own household, events and dojos${viewer.instructorDojoIds.length ? ', and your students' : ''}.`}</p>
        ) : groups.map(g => (
          <section key={g.key} className="space-y-2">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-ink-500">{g.label} <span className="font-normal">({g.items.length})</span></h2>
            <ul className="card divide-y divide-stone-100 p-0">
              {g.items.map(r => (
                <li key={`${r.kind}-${r.id}`}>
                  <Link to={resultHref(r, viewer)} className="block px-5 py-3 hover:bg-stone-50">
                    <span className="block font-semibold">{r.title}</span>
                    {r.subtitle && <span className="block text-sm text-ink-500">{r.subtitle}</span>}
                    {r.detail && <span className="block text-sm text-ink-500">{r.detail}</span>}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}
    </div>
  );
}
